import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Target, ShieldCheck, Layers, Repeat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container, SectionHeading, Eyebrow } from "@/components/common/Section";
import Reveal from "@/components/common/Reveal";
import CtaBand from "@/components/common/CtaBand";
import LicaraMock from "@/components/common/LicaraMock";

const approach = [
  { icon: Target, title: "Purpose-built", desc: "Every solution is designed around your exact mission and workflows — never forced from a template." },
  { icon: ShieldCheck, title: "Secure by design", desc: "Role-based access, audit trails, and encryption built in from the very first commit." },
  { icon: Layers, title: "Engineered to last", desc: "Modern, scalable architecture that grows with you and integrates with your stack." },
  { icon: Repeat, title: "Long-term partnership", desc: "We stay with you from launch through every iteration and improvement." },
];

const licaraHighlights = [
  "Permits & licensing — intake to renewal",
  "Field officer & citation management",
  "Law & code enforcement case management",
  "Secure, API-first, and compliance-ready",
];

const why = [
  { t: "Deep domain focus", d: "We specialize in mission-critical software for public operations." },
  { t: "Modern engineering", d: "Fast, reliable systems built on a proven, scalable stack." },
  { t: "Built for your users", d: "Clean, accessible interfaces designed around real workflows." },
  { t: "API-first", d: "Everything integrates cleanly with your existing systems." },
  { t: "Fast to launch", d: "Go live in weeks, not months, with hands-on onboarding." },
  { t: "Security-forward", d: "Public-sector-grade security and auditability throughout." },
];

export default function Solutions() {
  return (
    <div data-testid="solutions-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="hero-glow pointer-events-none absolute inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none absolute inset-0 opacity-60" />
        <Container className="relative py-20 md:py-28">
          <Reveal>
            <Eyebrow>Purpose-built solution specialists</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.06] tracking-[-0.02em] sm:text-5xl">
              Solutions engineered for the mission — not the template
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Sentira builds secure, mission-critical software tailored to how
              agencies and enterprises actually work. Our flagship product,
              Licara, shows what purpose-built focus delivers.
            </p>
          </Reveal>
        </Container>
      </section>

      {/* FLAGSHIP: LICARA */}
      <section className="pb-8">
        <Container>
          <Reveal>
            <div className="overflow-hidden rounded-[24px] border border-border bg-[hsl(220_30%_8%)]">
              <div className="grid items-center gap-10 p-8 md:p-12 lg:grid-cols-[1fr_1fr]">
                <div>
                  <span className="rounded-full border border-border bg-secondary px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Flagship product
                  </span>
                  <h2 className="mt-5 text-3xl font-semibold tracking-[-0.01em]">Licara</h2>
                  <p className="mt-1 text-sm font-medium text-primary">
                    Enforcement, permits & licensing platform
                  </p>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                    A unified, secure hub that replaces spreadsheets and legacy
                    systems — managing every permit, inspection, citation, and
                    case from a single source of truth.
                  </p>
                  <ul className="mt-6 space-y-3">
                    {licaraHighlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]">
                      <Link to="/solutions/licara" data-testid="solutions-explore-licara-button">
                        Explore Licara<ArrowRight className="ml-1.5 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button asChild size="lg" variant="secondary">
                      <Link to="/demo?interest=licara" data-testid="solutions-demo-button">Request a demo</Link>
                    </Button>
                  </div>
                </div>
                <div>
                  <LicaraMock />
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* APPROACH */}
      <section className="py-20 md:py-28">
        <Container>
          <SectionHeading eyebrow="Our approach" title="How we build purpose-built software" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {approach.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06}>
                <div className="h-full rounded-[var(--radius-card)] border border-border bg-card p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary">
                    <a.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h4 className="mt-4 text-base font-semibold">{a.title}</h4>
                  <p className="mt-1.5 text-sm text-muted-foreground">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHY SENTIRA */}
      <section className="pb-20 md:pb-28">
        <Container>
          <SectionHeading eyebrow="Why Sentira" title="Specialists, not generalists" />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {why.map((x, i) => (
              <Reveal key={x.t} delay={i * 0.05}>
                <div className="h-full rounded-[var(--radius-card)] border border-border bg-card p-6">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  <h4 className="mt-3 text-base font-semibold">{x.t}</h4>
                  <p className="mt-1.5 text-sm text-muted-foreground">{x.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <CtaBand
        title="Have a mission-critical challenge?"
        description="Tell us what you're trying to solve and we'll show you how a purpose-built solution fits."
        primaryLabel="Request a demo"
        secondaryLabel="Talk to us"
      />
    </div>
  );
}
